import { useState, useRef, useEffect } from "react";
import api from "../../utils/axiosConfig";
import { useModalAccessibility } from "../../hooks/useModalAccessibility";

const JoinGroupModal = ({ showModal, onClose, onJoinSuccess }) => {
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const modalRef = useRef(null);
  const inputsRef = useRef([]);

  const handleCloseModal = () => {
    onClose();
    setCode(["", "", "", "", "", ""]);
    setError("");
    setMsg("");
    setLoading(false);
  };

  useModalAccessibility({
    isOpen: showModal,
    onClose: handleCloseModal,
    modalRef,
  });

  // Auto-focus first input when modal opens
  useEffect(() => {
    if (showModal) {
      setError("");
      setMsg("");
      const timer = setTimeout(() => {
        inputsRef.current[0]?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showModal]);

  const handleCodeChange = (e, index) => {
    setError("");
    setMsg("");
    const rawValue = e.target.value;
    const value = rawValue.slice(-1); // Take last character entered

    if (value && !/^[0-9a-zA-Z]$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-advance to next input
    if (value && index < 5) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleJoinGroup();
    } else if (e.key === "Backspace") {
      setError("");
      if (!code[index] && index > 0) {
        // Current input is already empty, move to previous and clear it
        const newCode = [...code];
        newCode[index - 1] = "";
        setCode(newCode);
        inputsRef.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      e.preventDefault();
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    setError("");
    setMsg("");
    const pasteData = e.clipboardData.getData("text").trim();
    const cleaned = pasteData.replace(/[^0-9a-zA-Z]/g, "").slice(0, 6);
    if (!cleaned) return;

    const newCode = ["", "", "", "", "", ""];
    for (let i = 0; i < cleaned.length; i++) {
      newCode[i] = cleaned[i];
    }
    setCode(newCode);

    const nextIndex = Math.min(cleaned.length, 5);
    inputsRef.current[nextIndex]?.focus();
  };

  const handleJoinGroup = async () => {
    if (loading) return;
    setError("");
    setMsg("");
    const joinedCode = code.join("");
    if (joinedCode.length !== 6) {
      setError("Please enter a valid 6-digit code");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/groups/join", { code: joinedCode });
      
      if (res.data) {
        setMsg("✅ Group joined successfully!");
        setTimeout(() => {
          handleCloseModal();
          if (onJoinSuccess) onJoinSuccess();
        }, 800);
      }
    } catch (err) {
      console.error("Join error:", err);
      setError(err.response?.data?.message || "❌ Server error. Try again later.");
    } finally {
      setLoading(false);
    }
  };

  if (!showModal) return null;

  return (
    <div className="modal-overlay">
      <div 
        className="modal-content join-code-modal"
        ref={modalRef}
        tabIndex={-1}
        style={{ outline: "none" }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="join-group-title"
      >
        <span 
          className="close-btn" 
          onClick={handleCloseModal}
          aria-label="Close"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleCloseModal(); }}
        >
          &times;
        </span>
        <h2 id="join-group-title">Enter Event Code</h2>
        <p>Enter the 6-digit code to join the group</p>
        
        <form onSubmit={(e) => { e.preventDefault(); handleJoinGroup(); }}>
          <div className="code-inputs" onPaste={handlePaste}>
            {code.map((value, idx) => (
              <input
                key={idx}
                ref={(el) => (inputsRef.current[idx] = el)}
                id={`code-${idx}`}
                type="text"
                maxLength={1}
                inputMode="numeric"
                autoComplete="one-time-code"
                aria-label={`Digit ${idx + 1}`}
                value={value}
                onChange={(e) => handleCodeChange(e, idx)}
                onKeyDown={(e) => handleKeyDown(e, idx)}
              />
            ))}
          </div>
          <button 
            type="submit" 
            className="btn btn-primary next-btn" 
            disabled={loading}
            style={{ width: "100%", cursor: loading ? "not-allowed" : "pointer" }}
          >
            {loading ? "Joining..." : "Join Group"}
          </button>

          {/* Inline Error and Success Messages directly below the button */}
          {error && (
            <p 
              className="modal-inline-error"
              style={{
                color: "#ef4444",
                fontSize: "14px",
                fontWeight: "600",
                marginTop: "12px",
                marginBottom: "0",
                textAlign: "center",
                backgroundColor: "#fef2f2",
                padding: "8px 12px",
                borderRadius: "8px",
                border: "1px solid #fee2e2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px"
              }}
            >
              <span>⚠️</span> {error}
            </p>
          )}

          {msg && (
            <p 
              className="modal-inline-success"
              style={{
                color: "#16a34a",
                fontSize: "14px",
                fontWeight: "600",
                marginTop: "12px",
                marginBottom: "0",
                textAlign: "center",
                backgroundColor: "#f0fdf4",
                padding: "8px 12px",
                borderRadius: "8px",
                border: "1px solid #dcfce7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px"
              }}
            >
              {msg}
            </p>
          )}
        </form>
        <p style={{ marginTop: "14px" }}>You can also join via shared link</p>
      </div>
    </div>
  );
};

export default JoinGroupModal;
